"use client";

import { useEffect } from "react";
import { useImmer } from "use-immer";
import { useRouter } from "@bprogress/next";
import { useSearchParams } from "next/navigation";
import { cn } from "@/lib/utils";

export const FacilityTypeFilter = ({
  filters,
}: {
  filters: { key: string; title: string; shortTitle: string }[];
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [activeFilters, setActiveFilters] = useImmer<string[]>(() => {
    const typeParam = searchParams.get("type");
    return typeParam ? typeParam.split(",") : [];
  });

  // Update the URL's search params whenever activeFilters changes
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const current = searchParams.toString();

    searchParams.delete("type");
    if (activeFilters.length > 0) {
      searchParams.set("type", activeFilters.join(","));
    }

    // bez no-op nawigacji na mount — nadpisywała inne nawigacje startowe (np. odtworzenie izby z localStorage)
    if (searchParams.toString() === current) {
      return;
    }

    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    router.push(newUrl, { scroll: false });
  }, [activeFilters]);

  const handleFilterClick = (key: string) => {
    setActiveFilters((draft) => {
      const index = draft.indexOf(key);
      if (index > -1) {
        draft.splice(index, 1); // Remove if already selected
      } else {
        draft.push(key);
      }
    });
  };

  const typeColors: Record<MedicalFacilityType["key"], string> = {
    clinical: "bg-facility-clinical",
    "non-clinical": "bg-facility-non-clinical",
    military: "bg-facility-military",
    "health-center": "bg-facility-health-center",
  };

  return (
    <div className="no-scrollbar @512:justify-center flex flex-row items-center gap-2 overflow-x-auto">
      {filters.map(({ key, title, shortTitle }) => {
        const inactive =
          !activeFilters.includes(key) && activeFilters.length > 0;
        return (
          <div
            key={key}
            className={cn(
              "flex items-center gap-2 rounded-2xl bg-zinc-100 py-1 pr-3 pl-2 whitespace-nowrap transition-all hover:cursor-pointer hover:bg-zinc-200",
            )}
            onClick={() => handleFilterClick(key)}
          >
            <div
              className={cn(
                "h-2.5 w-2.5 rounded-[50%] transition-all",
                inactive ? "bg-[#C5C8CF]" : typeColors[key],
              )}
            ></div>
            <div
              className={cn(
                "text-xs text-zinc-900 transition-all",
                inactive && "text-[#778090]",
              )}
            >
              <span className={"@1216:inline hidden"}>{title}</span>
              <span className={"@1216:hidden inline"}>{shortTitle}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
